import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Plus } from "lucide-react";
import { api, errMsg, local } from "@/lib/api";
import { PageHeader, Tabs, Spinner } from "@/components/kit";
import { SkillSession } from "@/components/coping/SkillSession";

const CAT_LABELS = {
  anxiety: "Anxiety", panic: "Panic", grounding: "Grounding", mindfulness: "Mindfulness", depression: "Low Mood", sleep: "Sleep",
  anger: "Anger", wellness: "Wellness", self_harm: "Self-Harm Urges", cravings: "Cravings", intrusive: "Intrusive Thoughts",
  trauma: "Trauma", interpersonal: "Relationships", social: "Social", work_stress: "Work Stress", loneliness: "Loneliness",
  overwhelm: "Overwhelm & Burnout", self_esteem: "Self-Esteem", grief: "Grief & Loss", focus: "Focus & Restlessness", body_image: "Body Image",
};
const catLabel = (c) => CAT_LABELS[c] || c;
const NEW_CATS = new Set(["overwhelm", "self_esteem", "grief", "focus", "body_image"]);
const NEW_UNTIL = Date.parse("2026-11-02T00:00:00Z");

const SkillCard = ({ s, onOpen, onFav }) => (
  <div className="mm-card mm-card-hover p-4 cursor-pointer" onClick={() => onOpen(s)} data-testid={`coping-skill-${s.id}`}>
    <div className="flex justify-between items-start">
      <span className="text-3xl">{s.icon}</span>
      {!s.custom && (
        <button onClick={(e) => { e.stopPropagation(); onFav(s); }} data-testid={`coping-fav-${s.id}`} className="p-1" aria-label="Favourite">
          <Heart size={18} className={s.is_favorite ? "fill-current text-mm-error" : "text-mm-muted"} />
        </button>
      )}
    </div>
    <div className="font-semibold mt-2">{s.name}</div>
    <div className="text-xs text-mm-primary font-semibold">{catLabel(s.category)}{s.duration ? ` · ${s.duration}` : ""}</div>
    <p className="text-sm text-mm-muted mt-1">{s.description}</p>
  </div>
);

export default function Coping() {
  const [skills, setSkills] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [custom, setCustom] = useState(local.get("coping_custom", []));
  const [sel, setSel] = useState(null);
  const [newSkill, setNewSkill] = useState({ name: "", description: "" });

  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get("skill");
    if (!id || !skills) return;
    const s = skills.find((x) => x.id === id);
    if (s) setSel(s);
    setParams({}, { replace: true });
  }, [params, skills, setParams]);
  useEffect(() => {
    api.get("/coping-skills").then((r) => setSkills(r.data)).catch((e) => { setSkills([]); toast.error(errMsg(e, "Could not load skills")); });
  }, []);

  if (!skills) return <Spinner />;
  const customSkills = custom.map((c) => ({ ...c, category: "Custom", custom: true }));
  const all = [...skills, ...customSkills];
  const cats = ["All", "Favorites", ...[...new Set(skills.map((s) => s.category))], "Custom"];
  const countOf = (c) => (c === "All" ? all.length : all.filter((s) => (c === "Favorites" ? s.is_favorite : s.category === c)).length);
  const ql = q.toLowerCase();
  const list = all.filter((s) => (cat === "All" || (cat === "Favorites" ? s.is_favorite : s.category === cat))
    && (s.name.toLowerCase().includes(ql) || (s.description || "").toLowerCase().includes(ql)));

  const toggleFav = async (s) => {
    const is_favorite = !s.is_favorite;
    setSkills((l) => l.map((x) => (x.id === s.id ? { ...x, is_favorite } : x)));
    try { await api.put(`/coping-skills/${s.id}`, { is_favorite }); }
    catch (e) { setSkills((l) => l.map((x) => (x.id === s.id ? { ...x, is_favorite: !is_favorite } : x))); toast.error(errMsg(e)); }
  };
  const addCustom = () => {
    if (!newSkill.name.trim()) return;
    const n = [...custom, { id: `custom-${Date.now()}`, name: newSkill.name.trim(), description: newSkill.description.trim(), icon: "⭐" }];
    setCustom(n); local.set("coping_custom", n); setNewSkill({ name: "", description: "" }); toast.success("Custom skill added");
  };

  return (
    <div data-testid="coping-page">
      <PageHeader back title="Coping Skills" subtitle={`${skills.length} evidence-based skills · tap one to start`} />
      <input className="mm-input mb-4" placeholder="Search skills…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="coping-search-input" />
      <Tabs items={cats.map((c) => ({ id: c, name: c === "All" || c === "Favorites" || c === "Custom" ? c : catLabel(c), count: countOf(c), tag: NEW_CATS.has(c) && Date.now() < NEW_UNTIL ? "New" : null }))} value={cat} onChange={setCat} testPrefix="coping-cat" />
      {cat === "Custom" && (
        <div className="mm-card p-4 mb-4 flex flex-col sm:flex-row gap-2">
          <input className="mm-input" placeholder="Skill name" value={newSkill.name} onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })} data-testid="coping-custom-name" />
          <input className="mm-input" placeholder="How it helps" value={newSkill.description} onChange={(e) => setNewSkill({ ...newSkill, description: e.target.value })} data-testid="coping-custom-desc" />
          <button className="mm-btn" onClick={addCustom} data-testid="coping-custom-add"><Plus size={16} />Add</button>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mm-stagger" data-testid="coping-skill-grid">
        {list.map((s) => <SkillCard key={s.id} s={s} onOpen={setSel} onFav={toggleFav} />)}
      </div>
      {!list.length && <p className="text-sm text-mm-muted" data-testid="coping-empty">{cat === "Favorites" ? "Tap the heart on a skill to save it here." : "No skills match."}</p>}
      {sel && <SkillSession skill={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
